import React, { useContext, useLayoutEffect, useRef } from 'react'
import classnames from 'classnames'
import { useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import Header from './Header'
import { Container, Row } from 'react-bootstrap'
import { scrollAdminContentToTop } from '../../utils/utility'
import { LayoutContext } from '../../utils/contexts'

/** Same experience Build/Manage subsection nav — keep scroll position. */
const isSameExperienceSubnav = (prevPath, nextPath) => {
  const re = /^\/admin\/experiences\/([^/]+)\/(build|manage)(?:\/[^/]*)?$/
  const prev = (prevPath || '').match(re)
  const next = (nextPath || '').match(re)
  if (!prev || !next) {
    return false
  }
  // Same quest + same mode (build↔build or manage↔manage)
  return prev[1] === next[1] && prev[2] === next[2]
}

/** List tabs stay on normal scrollable chrome — not the locked editor shell. */
const EXPERIENCE_LIST_PATHS = new Set([
  '/admin/experiences',
  '/admin/experiences/',
  '/admin/experiences/active',
  '/admin/experiences/archive',
])

/**
 * Experience create/detail (and nested Build/Manage grandchildren) — locked
 * chrome; only the right pane scrolls. Parent list routes must not match.
 */
const isExperienceEditorPath = (pathname) => {
  if (!pathname || EXPERIENCE_LIST_PATHS.has(pathname)) {
    return false
  }
  return (
    pathname.startsWith('/admin/experiences/create-new') ||
    /^\/admin\/experiences\/[^/]+/.test(pathname)
  )
}

const Layout = ({ children }) => {
  const location = useLocation()
  const prevPathRef = useRef(location.pathname)
  const experienceEditor = isExperienceEditorPath(location.pathname)
  const { showSidebar } = useContext(LayoutContext)

  // Scroll to top when route changes (pathname or query), except Build/Manage
  // subsection switches that would otherwise jump the page on every accordion click.
  useLayoutEffect(() => {
    const prevPath = prevPathRef.current
    prevPathRef.current = location.pathname

    if (isSameExperienceSubnav(prevPath, location.pathname)) {
      return
    }

    scrollAdminContentToTop()
  }, [location.pathname, location.search, location.key])

  return (
    <Container fluid className='custom-container p-0 vh-100 overflow-hidden'>
      <Row
        className={`experience-shell-row position-relative m-0 h-100 w-100 no-gutters${
          experienceEditor ? ' overflow-hidden' : ' overflow-auto'
        }`}>
        {/* Fixed admin gnav (v7 .gnav) — out of document flow.
            On mobile, .hide moves the whole overlay off-screen so it cannot
            sit above the hamburger (header lives in a lower stacking context). */}
        <div
          className={classnames(
            'sidebar-content experience-gnav-col px-0 content-gutter',
            { hide: !showSidebar },
          )}>
          <Sidebar />
        </div>
        {/* In-flow spacer matching gnav width so content clears the fixed nav */}
        <div
          className='experience-gnav-spacer d-none d-lg-block'
          aria-hidden='true'
        />
        <div
          className={`experience-main-col px-0${
            experienceEditor ? ' h-100 d-flex flex-column' : ''
          }`}>
          <div
            id='content'
            className={`body-content${
              experienceEditor
                ? ' experience-content-scroll flex-grow-1'
                : ' overflow-auto pb-5'
            }`}>
            <Header />
            <div
              className={`pt-0 pt-sm-sidebar${
                experienceEditor ? ' experience-content-children' : ''
              }`}>
              {children}
            </div>
          </div>
        </div>
      </Row>
    </Container>
  )
}

export default Layout
